import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";

// UPI confirmations can take well over a minute — 18s was giving customers a
// false "failed" page while the payment was still completing on PhonePe's side.
// Backend also runs an independent reconciliation sweep, so this is a UX window,
// not the only safety net — but it should still cover the common case.
const MAX_POLLS = 30;
const POLL_MS   = 3000;

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { applyPaymentResult } = useAuth();
  const started = useRef(false);

  const merchantOrderId = searchParams.get("merchantOrderId");

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!merchantOrderId || !/^LK[A-Z0-9]{8,24}$/.test(merchantOrderId)) {
      navigate("/app", { replace: true });
      return;
    }

    poll(0);
  }, []); // eslint-disable-line

  async function poll(attempt) {
    try {
      const { data } = await api.get(`/api/payment/phonepe/status/${merchantOrderId}`);

      if (data.status === "COMPLETED") {
        applyPaymentResult(data);
        navigate("/payment/success", {
          replace: true,
          state: {
            transactionId: data.transactionId || null,
            callsAdded:    data.callsAdded    || null,
            amount:        data.amount        || null,
          },
        });
        return;
      }

      if (data.status === "FAILED") {
        navigate("/payment/failed", { replace: true });
        return;
      }

      // PENDING — keep polling
      if (attempt < MAX_POLLS) {
        setTimeout(() => poll(attempt + 1), POLL_MS);
      } else {
        navigate("/payment/failed", { replace: true, state: { reason: "timeout", merchantOrderId } });
      }
    } catch {
      if (attempt < 2) {
        setTimeout(() => poll(attempt + 1), 2000);
      } else {
        navigate("/payment/failed", { replace: true, state: { reason: "timeout", merchantOrderId } });
      }
    }
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#0F172A",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "system-ui, sans-serif", padding: 24,
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

      {/* Spinner */}
      <div style={{
        width: 56, height: 56, borderRadius: "50%",
        border: "3px solid rgba(95,37,159,0.2)",
        borderTop: "3px solid #5f259f",
        animation: "spin 0.9s linear infinite",
        marginBottom: 28,
      }} />

      <div style={{ fontSize: 18, fontWeight: 700, color: "#F1F5F9", marginBottom: 10 }}>
        Verifying your payment
      </div>
      <div style={{ fontSize: 14, color: "#64748B", textAlign: "center", maxWidth: 300, lineHeight: 1.6, animation: "pulse 2s ease-in-out infinite" }}>
        Confirming with PhonePe — please don't close this page.
      </div>
    </div>
  );
}
